import { createReducer } from '@reduxjs/toolkit';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { legacySurveyActions } from '~/behaviors/actions/legacy_survey.actions';
import { surveyReminderActions } from '~/behaviors/actions/survey_reminder.actions';
import { surveyActions } from '~/behaviors/actions/survey.actions';
import { handleUnsafeOptionalChaining } from '~/tools/lint/handleUnsafeOptionalChaining';

const defaultState = {
  surveys: [],
  selectedSurvey: null,
  selectedSurveyMode: null,
  selectedReminder: {},
  isLoadingSurveyReminder: false,
  selectedDeleteReminder: {
    id: null,
    surveyId: null,
  },
};

const reset = () => defaultState;

const replaceSurveys = (state, action) => {
  const surveys = action?.payload?.data?.entities?.surveys || {};
  const sortedSurveyIds = action?.payload?.data?.result || [];

  state.surveys = sortedSurveyIds.map((surveyId) => surveys[surveyId]);
};

const updateSurveyRemindersCount = (state, action) => {
  const { surveyId, changeCount } = handleUnsafeOptionalChaining(action?.payload);
  const survey = state.surveys.find((s) => s.id === surveyId);
  if (!survey) return;

  survey.remindersCount += changeCount;
};

const resetSurvey = (state) => {
  state.selectedSurvey = defaultState.selectedSurvey;
  state.selectedSurveyMode = defaultState.selectedSurveyMode;
};

const selectSurvey = (mode) => (state, action) => {
  const surveyId = action?.payload?.surveyId;
  const survey = state?.surveys?.find((s) => s.id === surveyId);

  const cantChange = !mode || !survey;
  const nothingToChange = state.selectedSurveyMode === mode && state.selectedSurvey.id === surveyId;

  if (cantChange || nothingToChange) return state;

  state.selectedSurvey = survey;
  state.selectedSurveyMode = mode;
};

const selectReminder = (state, action) => {
  if (!action?.payload?.surveyReminder) return;
  state.selectedReminder = action?.payload?.surveyReminder;
};

const resetReminder = (state) => {
  state.selectedReminder = defaultState.selectedReminder;
};

const selectDeleteReminder = (state, action) => {
  const { id, surveyId } = action?.payload || {};

  state.selectedDeleteReminder = { id, surveyId };
};

const resetDeleteReminder = (state) => {
  state.selectedDeleteReminder = defaultState.selectedDeleteReminder;
};

const updateSurveyStatus = (state, action) => {
  const updatedSurvey = Object.keys(action?.payload?.entities?.surveys).map(
    (surveyId) => action?.payload?.entities?.surveys[surveyId],
  )[0];
  state.surveys = state.surveys.map((survey) => {
    if (survey.id === updatedSurvey.id) {
      survey.statusId = updatedSurvey.surveyStatusId;
      return survey;
    }
    return survey;
  });
};

const merge = (updates) => (state) => ({
  ...state,
  ...updates,
});

const mergeConvertSurveyResults = (state, action) => {
  if (action?.payload) {
    const { surveyId, isContinuous, lifecycleSurveyTypeId } = action.payload;
    const surveyIndex = state.surveys.findIndex((survey) => survey.id === surveyId);
    state.surveys[surveyIndex] = {
      ...state.surveys[surveyIndex],
      endTimeUTC: null,
      isContinuous,
      lifecycleSurveyTypeId,
    };
  }
};

const surveyListUI = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(legacySurveyActions.resetSurveyListPage, reset)
    .addCase(legacySurveyActions.fetchDashboardSurvey.fulfilled, replaceSurveys)
    .addCase(surveyReminderActions.upsertReminder.fulfilled, updateSurveyRemindersCount)
    .addCase(surveyReminderActions.deleteReminder.fulfilled, updateSurveyRemindersCount)
    .addCase(legacySurveyActions.deselectSurvey, resetSurvey)
    .addCase(legacySurveyActions.selectPreviewSurvey, selectSurvey('preview'))
    .addCase(legacySurveyActions.selectUpdateSurvey, selectSurvey('update'))
    .addCase(legacySurveyActions.selectDuplicateSurvey, selectSurvey('duplicate'))
    .addCase(legacySurveyActions.selectDeleteSurvey, selectSurvey('delete'))
    .addCase(legacySurveyActions.selectCancelSurvey, selectSurvey('cancel'))
    .addCase(surveyReminderActions.openNewReminder.fulfilled, selectReminder)
    .addCase(surveyReminderActions.openDuplicateReminder.fulfilled, selectReminder)
    .addCase(surveyReminderActions.openExistingReminder.fulfilled, selectReminder)
    .addCase(surveyReminderActions.resetReminder, resetReminder)
    .addCase(surveyActions.updateSurveyStatus.fulfilled, updateSurveyStatus)
    .addCase(surveyReminderActions.findSurveyRemindersBySurveyId.pending, merge({ isLoadingSurveyReminder: true }))
    .addCase(surveyReminderActions.findSurveyRemindersBySurveyId.fulfilled, merge({ isLoadingSurveyReminder: false }))
    .addCase(surveyReminderActions.findSurveyRemindersBySurveyId.rejected, merge({ isLoadingSurveyReminder: false }))
    .addCase(surveyReminderActions.openConfirmDeleteReminder, selectDeleteReminder)
    .addCase(surveyReminderActions.resetDeleteReminder, resetDeleteReminder)
    .addCase(surveyActions.convertToContinuous.fulfilled, mergeConvertSurveyResults);
});

export default surveyListUI;
